import { hasNativeBridge } from "util/userAgent";
import { getLocation } from "common/nativeBridge";
import { createContext, useContext, useEffect, useState } from "react";
import { callGetNearestRegion } from "api/MuyeonApi";

/**
 * 관심 지역 다중 등록 모델 (이용식당 패턴).
 *  - regions: 등록된 지역 배열 (최대 5)
 *  - repCode: 대표(현재 활성) 지역 코드 → 홈/리스트가 이 지역으로 필터
 *  - 지역 item: { kind:'sigungu'|'sido', code, name }
 *      sigungu: code=5자리 시군구코드, name="서울 강남구"  (수도권 디테일)
 *      sido:    code=2자리 시도prefix, name="부산"          (지방 간략)
 *  - localStorage 'muyeonRegions' 영속.
 */
const RegionContext = createContext(null);

export const MAX_REGIONS = 5;
export const METRO_SIDO = ["서울", "경기", "인천"]; // 수도권(시군구 디테일)
const STORAGE = "muyeonRegions";

const read = () => {
  try {
    const s = localStorage.getItem(STORAGE);
    const v = s ? JSON.parse(s) : null;
    return { regions: v?.regions || [], repCode: v?.repCode || null };
  } catch {
    return { regions: [], repCode: null };
  }
};

/**
 * 현재 좌표 한 벌. 실패하면 null.
 *
 * ★ 앱에서는 네이티브 브릿지를 쓴다.
 *   WKWebView 의 navigator.geolocation 은 네이티브 위치 권한과 **별개로** 웹 오리진
 *   프롬프트를 한 번 더 띄운다. 그래서 최초 설치 때 네이티브가 묻고 웹뷰가 또 묻는
 *   일이 생겼다. 브릿지를 쓰면 네이티브가 이미 받은 권한을 그대로 재사용한다.
 *   (브라우저에는 브릿지가 없으므로 기존대로 navigator.geolocation)
 */
const readCoords = () =>
  new Promise((resolve) => {
    if (hasNativeBridge()) {
      getLocation()
        .then((res) => {
          const d = Array.isArray(res?.data) ? res.data[0] : res?.data;
          const lat = Number(d?.latitude);
          const lng = Number(d?.longitude);
          // 권한이 없으면 네이티브가 "Unknown" 을 보낸다 → NaN 으로 걸러진다.
          const ok = Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;
          resolve(ok ? { lat, lng } : null);
        })
        .catch(() => resolve(null));
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 8000, maximumAge: 600000 }
    );
  });

export const RegionProvider = ({ children }) => {
  const init = read();
  const [regions, setRegions] = useState(init.regions);
  const [repCode, setRepCode] = useState(init.repCode);

  const persist = (rs, rep) => {
    try {
      localStorage.setItem(STORAGE, JSON.stringify({ regions: rs, repCode: rep }));
    } catch {}
  };

  // 지역 추가 (최대 5, 중복 무시). 첫 등록이면 대표 지정.
  const addRegion = (item) => {
    if (regions.some((r) => r.code === item.code)) {
      return { ok: true, already: true };
    }
    if (regions.length >= MAX_REGIONS) {
      return { ok: false, reason: "max" };
    }
    const next = [...regions, item];
    const rep = repCode || item.code;
    setRegions(next);
    setRepCode(rep);
    persist(next, rep);
    return { ok: true };
  };

  // 지역 삭제 (대표는 삭제 불가)
  const removeRegion = (code) => {
    if (code === repCode) return { ok: false, reason: "rep" };
    const next = regions.filter((r) => r.code !== code);
    setRegions(next);
    persist(next, repCode);
    return { ok: true };
  };

  // 대표(활성) 지역 변경
  const setRepresentative = (code) => {
    setRepCode(code);
    persist(regions, code);
  };

  const active = regions.find((r) => r.code === repCode) || null;

  // 첫 진입(등록 지역 없음) 시 현재 위치로 대표 지역 자동 등록.
  useEffect(() => {
    if (regions.length > 0) return;
    let alive = true;
    readCoords().then((coords) => {
      if (!alive || !coords) return;
      callGetNearestRegion(coords.lat, coords.lng)
        .then((res) => {
          const d = res?.data;
          if (!d?.code) return;
          const isMetro = METRO_SIDO.includes(d.sido);
          const item = isMetro
            ? { kind: "sigungu", code: d.code, name: d.name }
            : { kind: "sido", code: String(d.code).slice(0, 2), name: d.sido };
          addRegion(item);
        })
        .catch(() => {});
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RegionContext.Provider
      value={{ regions, repCode, active, addRegion, removeRegion, setRepresentative }}
    >
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = () => useContext(RegionContext);
